import { Box, Paper, CircularProgress, Alert } from '@mui/material';
import { useRpcClientCall } from '@/providers/backendClient';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import type { Components } from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ReactJson from 'react-json-view';
import { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';

interface ReportsViewerProps {
  filePath: string;
  currentPath: string;
}

/**
 * Resolves a relative markdown link to an absolute path
 * @param linkPath - The link path from the markdown (e.g., "./01-problem.md", "../README.md", "01-problem.md")
 * @param currentFilePath - The current file's path (e.g., "2025-12-05-1-ExpiredAuth0TokensConnectivityIssue/README.md")
 * @returns The resolved absolute path or null if it's not a markdown link
 */
const resolveMarkdownLink = (
  linkPath: string,
  currentFilePath: string,
): string | null => {
  // Only handle .md and .markdown files
  if (!linkPath.match(/\.(md|markdown)$/i)) {
    return null;
  }

  // Remove query strings and anchors
  const cleanPath = linkPath.split('?')[0].split('#')[0];

  // Get the directory of the current file
  const currentDir = currentFilePath.includes('/')
    ? currentFilePath.substring(0, currentFilePath.lastIndexOf('/'))
    : '';

  // Handle different link formats
  let resolvedPath: string;

  if (cleanPath.startsWith('./')) {
    // Relative to current directory: ./01-problem.md
    resolvedPath = currentDir
      ? `${currentDir}/${cleanPath.slice(2)}`
      : cleanPath.slice(2);
  } else if (cleanPath.startsWith('../')) {
    // Parent directory: ../README.md
    const parts = currentDir.split('/');
    let relativePath = cleanPath;
    while (relativePath.startsWith('../')) {
      parts.pop();
      relativePath = relativePath.slice(3);
    }
    resolvedPath =
      parts.length > 0
        ? `${parts.join('/')}/${relativePath}`
        : relativePath;
  } else if (cleanPath.startsWith('/')) {
    // Absolute from root: /2025-12-05-1-ExpiredAuth0TokensConnectivityIssue/README.md
    resolvedPath = cleanPath.slice(1);
  } else {
    // Relative to current directory: 01-problem.md
    resolvedPath = currentDir
      ? `${currentDir}/${cleanPath}`
      : cleanPath;
  }

  return resolvedPath;
};

/**
 * Detects the file type based on the file extension
 */
const getFileType = (filePath: string): 'markdown' | 'json' | 'code' | 'text' => {
  const extension = filePath.split('.').pop()?.toLowerCase() || '';
  
  if (['md', 'markdown'].includes(extension)) {
    return 'markdown';
  }
  
  if (extension === 'json') {
    return 'json';
  }
  
  // Code file extensions
  if (['ts', 'tsx', 'js', 'jsx', 'py', 'java', 'cpp', 'c', 'cs', 'go', 'rs', 'php', 'rb', 'swift', 'kt', 'scala', 'sh', 'bash', 'yaml', 'yml', 'xml', 'html', 'css', 'scss', 'less', 'sql', 'graphql', 'vue', 'svelte'].includes(extension)) {
    return 'code';
  }
  
  // Text file extensions (raw text files like logs, git output, etc.)
  // Files without extensions or with unknown extensions will also default to 'text'
  if (['txt', 'log', 'out', 'err', 'diff', 'patch'].includes(extension)) {
    return 'text';
  }
  
  // Default to text for files without recognized extensions
  return 'text';
};

/**
 * Gets the language name for syntax highlighting based on file extension
 */
const getLanguage = (filePath: string): string => {
  const extension = filePath.split('.').pop()?.toLowerCase() || '';
  
  const languageMap: Record<string, string> = {
    ts: 'typescript',
    tsx: 'tsx',
    js: 'javascript',
    jsx: 'jsx',
    py: 'python',
    java: 'java',
    cpp: 'cpp',
    c: 'c',
    cs: 'csharp',
    go: 'go',
    rs: 'rust',
    php: 'php',
    rb: 'ruby',
    swift: 'swift',
    kt: 'kotlin',
    scala: 'scala',
    sh: 'bash',
    bash: 'bash',
    yaml: 'yaml',
    yml: 'yaml',
    xml: 'xml',
    html: 'html',
    css: 'css',
    scss: 'scss',
    less: 'less',
    sql: 'sql',
    graphql: 'graphql',
    vue: 'vue',
    svelte: 'svelte',
  };
  
  return languageMap[extension] || 'text';
};

/**
 * Mermaid diagram component that renders Mermaid syntax as diagrams
 */
const MermaidDiagram = ({ children }: { children: string }) => {
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const renderIdRef = useRef(0);

  useEffect(() => {
    let isMounted = true;
    const currentRenderId = ++renderIdRef.current;

    const renderDiagram = async () => {
      try {
        // Reset state
        setError(null);
        setSvgContent(null);

        // Initialize Mermaid with transparent backgrounds
        mermaid.initialize({
          startOnLoad: false,
          theme: 'dark',
          securityLevel: 'loose',
          fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
          themeVariables: {
            primaryColor: 'transparent',
            primaryTextColor: '#c9d1d9',
            primaryBorderColor: '#30363d',
            lineColor: '#30363d',
            secondaryColor: 'transparent',
            tertiaryColor: 'transparent',
            background: '#0d1117',
            mainBkg: 'transparent',
            secondBkg: 'transparent',
            textColor: '#c9d1d9',
            // Remove all node background colors
            cScale0: 'transparent',
            cScale1: 'transparent',
            cScale2: 'transparent',
            // Ensure text is always readable
            cScaleLabel0: '#c9d1d9',
            cScaleLabel1: '#c9d1d9',
            cScaleLabel2: '#c9d1d9',
          },
        });

        // Generate a unique ID for this diagram
        const uniqueId = `mermaid-diagram-${currentRenderId}-${Date.now()}`;

        // Create a temporary container outside of React's DOM tree
        const tempContainer = document.createElement('div');
        tempContainer.style.position = 'absolute';
        tempContainer.style.left = '-9999px';
        tempContainer.style.top = '-9999px';
        tempContainer.className = 'mermaid';
        tempContainer.id = uniqueId;
        document.body.appendChild(tempContainer);

        try {
          // Render the diagram using the temporary container
          const { svg } = await mermaid.render(uniqueId, children);

          // Post-process SVG to remove all background colors and ensure text is readable
          // Use a more precise approach: parse SVG structure and only modify background elements
          let processedSvg = svg;
          
          // Remove fills from rectangles and polygons (these are node backgrounds)
          // Match: <rect ... fill="color" ...> or <polygon ... fill="color" ...>
          processedSvg = processedSvg.replace(
            /<(rect|polygon)[^>]*fill="[^"]*"[^>]*>/gi,
            (match) => {
              // Only remove fill if it's not already transparent or none
              if (match.includes('fill="transparent"') || match.includes('fill="none"')) {
                return match;
              }
              return match.replace(/fill="[^"]*"/, 'fill="transparent"');
            }
          );
          
          // Ensure all text is readable (light gray on dark background)
          processedSvg = processedSvg.replace(
            /<text[^>]*>/gi,
            (match) => {
              if (match.includes('fill=')) {
                return match.replace(/fill="[^"]*"/, 'fill="#c9d1d9"');
              }
              return match.replace('>', ' fill="#c9d1d9">');
            }
          );
          
          // Also handle inline style fills
          processedSvg = processedSvg.replace(/fill:#[0-9a-fA-F]{6}/gi, (match) => {
            // Don't replace if it's in a text element context (we handle text separately)
            return match;
          });
          
          // Replace common problematic background colors with transparent
          const colorReplacements = [
            ['#ffcccc', 'transparent'], // Light pink
            ['#ffffcc', 'transparent'], // Light yellow
            ['#ff9999', 'transparent'], // Light red
            ['#ffcc99', 'transparent'], // Light orange
            ['#ccffcc', 'transparent'], // Light green
            ['#ccccff', 'transparent'], // Light blue
            ['#e6f3ff', 'transparent'], // Very light blue
          ];
          
          colorReplacements.forEach(([from, to]) => {
            processedSvg = processedSvg.replace(
              new RegExp(`fill="${from}"`, 'gi'),
              `fill="${to}"`
            );
            processedSvg = processedSvg.replace(
              new RegExp(`fill:${from}`, 'gi'),
              `fill:${to}`
            );
          });

          // Only update state if component is still mounted and this is still the current render
          if (isMounted && currentRenderId === renderIdRef.current) {
            setSvgContent(processedSvg);
          }
        } finally {
          // Always clean up the temporary container safely
          try {
            if (tempContainer.parentNode === document.body) {
              document.body.removeChild(tempContainer);
            }
          } catch (cleanupErr) {
            // Ignore cleanup errors - container might have already been removed
            console.warn('Error cleaning up temporary Mermaid container:', cleanupErr);
          }
        }
      } catch (err: any) {
        console.error('Error rendering Mermaid diagram:', err);
        if (isMounted && currentRenderId === renderIdRef.current) {
          setError(err.message || 'Failed to render diagram');
        }
      }
    };

    renderDiagram();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [children]);

  if (error) {
    return (
      <Box
        sx={{
          margin: '16px 0',
          padding: '16px',
          backgroundColor: '#161b22',
          borderRadius: '6px',
          border: '1px solid #30363d',
        }}
      >
        <Box
          sx={{
            color: '#f85149',
            marginBottom: '8px',
            fontWeight: 600,
          }}
        >
          Error rendering Mermaid diagram
        </Box>
        <Box
          sx={{
            color: '#8b949e',
            fontSize: '85%',
            fontFamily:
              'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
            whiteSpace: 'pre-wrap',
          }}
        >
          {children}
        </Box>
      </Box>
    );
  }

  if (svgContent) {
    return (
      <Box
        sx={{
          margin: '16px 0',
          padding: '16px',
          backgroundColor: '#161b22',
          borderRadius: '6px',
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          '& svg': {
            maxWidth: '100%',
            height: 'auto',
            // Remove all background fills from all shapes (rectangles, polygons, paths used as fills)
            '& rect, & polygon': {
              fill: 'transparent !important',
            },
            // Also remove fills from paths that are likely backgrounds (not lines/arrows)
            '& path[fill]:not([stroke])': {
              fill: 'transparent !important',
            },
            // Ensure all text is readable
            '& text, & tspan': {
              fill: '#c9d1d9 !important',
              color: '#c9d1d9 !important',
            },
          },
        }}
        dangerouslySetInnerHTML={{ __html: svgContent }}
      />
    );
  }

  return (
    <Box
      sx={{
        margin: '16px 0',
        padding: '16px',
        backgroundColor: '#161b22',
        borderRadius: '6px',
        overflow: 'auto',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      {!error && (
        <Box
          sx={{
            color: '#8b949e',
            fontStyle: 'italic',
            textAlign: 'center',
          }}
        >
          Loading diagram...
        </Box>
      )}
    </Box>
  );
};

export const ReportsViewer = ({
  filePath,
  currentPath,
}: ReportsViewerProps) => {
  const { data, isLoading, error } = useRpcClientCall(
    'PUBLIC_ADMIN_reports_get',
    [filePath],
    {},
  );

  // Custom link component that generates properly formatted links
  const components: Components = {
    pre: ({ children, ...props }) => {
      // Check if children is a code element with mermaid language
      if (children && typeof children === 'object' && 'props' in children) {
        const codeProps = (children as any).props;
        const className = codeProps?.className || '';
        const isMermaid = className.includes('language-mermaid');
        
        if (isMermaid) {
          const codeContent = codeProps?.children;
          const content = Array.isArray(codeContent)
            ? codeContent.join('')
            : String(codeContent || '');
          
          return <MermaidDiagram>{content}</MermaidDiagram>;
        }
      }
      
      // Default pre rendering
      return <pre {...props}>{children}</pre>;
    },
    code: ({ inline, className, children, ...props }) => {
      // Check if this is a Mermaid code block
      const isMermaid = !inline && className?.includes('language-mermaid');
      
      if (isMermaid) {
        // Extract the code content (children might be an array)
        const codeContent = Array.isArray(children)
          ? children.join('')
          : String(children);
        
        return <MermaidDiagram>{codeContent}</MermaidDiagram>;
      }

      // Default code rendering for inline code and non-mermaid blocks
      return (
        <code className={className} {...props}>
          {children}
        </code>
      );
    },
    a: ({ href, children, ...props }) => {
      if (!href) {
        return <a {...props}>{children}</a>;
      }

      // Check if it's a markdown file link
      const isMarkdownLink = href.match(/\.(md|markdown)$/i);
      if (!isMarkdownLink) {
        // Not a markdown link, use regular anchor tag
        return (
          <a href={href} {...props} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        );
      }

      // Resolve relative links to absolute paths
      let resolvedPath: string | null = null;

      // Check if it's already a reports path
      if (href.startsWith('/reports/view/')) {
        resolvedPath = decodeURIComponent(href.slice('/reports/view/'.length));
      } else if (href.startsWith('/reports/')) {
        resolvedPath = decodeURIComponent(href.slice('/reports/'.length));
      } else if (href.startsWith('#/reports/view/')) {
        resolvedPath = decodeURIComponent(href.slice('#/reports/view/'.length));
      } else if (href.startsWith('#/reports/')) {
        resolvedPath = decodeURIComponent(href.slice('#/reports/'.length));
      } else if (href.startsWith('http://') || href.startsWith('https://')) {
        // External URL - check if it's our origin
        try {
          const url = new URL(href);
          if (url.origin === window.location.origin) {
            const pathname = url.pathname;
            if (pathname.match(/\.(md|markdown)$/i) || (pathname.includes('/') && !pathname.startsWith('/reports/'))) {
              resolvedPath = pathname.startsWith('/') ? pathname.slice(1) : pathname;
            } else if (url.hash && url.hash.startsWith('#/reports/')) {
              resolvedPath = decodeURIComponent(url.hash.slice('#/reports/'.length));
            }
          }
        } catch {
          // Invalid URL, treat as external
        }
      } else {
        // Relative link - resolve it
        resolvedPath = resolveMarkdownLink(href, currentPath);
      }

      if (resolvedPath) {
        // Generate properly formatted link for dialog view
        // Check if we're in the dialog view (URL contains /reports/view/)
        const currentHash = window.location.hash;
        const isInDialogView = currentHash.includes('/reports/view/');
        const linkPath = isInDialogView
          ? `/reports/view/${encodeURIComponent(resolvedPath)}`
          : `/reports/${encodeURIComponent(resolvedPath)}`;
        
        return (
          <Link to={linkPath} {...props}>
            {children}
          </Link>
        );
      }

      // Fallback to regular anchor tag
      return (
        <a href={href} {...props} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    },
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load report:{' '}
        {error instanceof Error ? error.message : 'Unknown error'}
      </Alert>
    );
  }

  const dataResult = data as
    | { success: boolean; content?: string; error?: string }
    | undefined;

  if (!dataResult?.success || !dataResult?.content) {
    return (
      <Alert severity="warning">
        {dataResult?.error || 'Report not found'}
      </Alert>
    );
  }

  const fileType = getFileType(filePath);

  // Render JSON files with react-json-view
  if (fileType === 'json') {
    try {
      const jsonData = JSON.parse(dataResult.content);
      return (
        <Paper
          sx={{
            p: 0,
            height: '100%',
            overflow: 'auto',
            backgroundColor: '#1e1e1e',
          }}
        >
          <Box sx={{ p: 2 }}>
            <ReactJson
              src={jsonData}
              theme="monokai"
              displayDataTypes={false}
              enableClipboard={true}
              collapsed={2}
              style={{
                backgroundColor: 'transparent',
                fontFamily: 'monospace',
              }}
            />
          </Box>
        </Paper>
      );
    } catch {
      // If JSON parsing fails, fall through to code viewer
    }
  }

  // Render code files with syntax highlighting
  if (fileType === 'code' || fileType === 'text') {
    const language = getLanguage(filePath);
    return (
      <Paper
        sx={{
          p: 0,
          height: '100%',
          overflow: 'auto',
          backgroundColor: '#1e1e1e',
        }}
      >
        <SyntaxHighlighter
          language={language}
          style={vscDarkPlus}
          customStyle={{
            margin: 0,
            padding: '16px',
            backgroundColor: '#1e1e1e',
            fontSize: '14px',
          }}
          showLineNumbers
          wrapLines
        >
          {dataResult.content}
        </SyntaxHighlighter>
      </Paper>
    );
  }

  // Render markdown files with ReactMarkdown
  return (
    <Paper
      sx={{
        p: 0,
        height: '100%',
        overflow: 'auto',
        backgroundColor: 'rgb(13, 17, 23)',
      }}
    >
      <Box
        sx={{
          padding: '32px',
          backgroundColor: 'rgb(13, 17, 23)',
          color: '#c9d1d9',
          '& > *': {
            marginBottom: '16px',
          },
          '& > *:last-child': {
            marginBottom: 0,
          },
          '& h1, & h2, & h3, & h4, & h5, & h6': {
            marginTop: '24px',
            marginBottom: '16px',
            color: '#c9d1d9',
            fontWeight: 600,
          },
          '& h1': {
            fontSize: '2em',
            borderBottom: '1px solid #30363d',
            paddingBottom: '0.3em',
          },
          '& h2': {
            fontSize: '1.5em',
            borderBottom: '1px solid #30363d',
            paddingBottom: '0.3em',
          },
          '& p': {
            marginBottom: '16px',
            lineHeight: 1.6,
          },
          '& ul, & ol': {
            marginBottom: '16px',
            paddingLeft: '24px',
          },
          '& li': {
            marginBottom: '8px',
          },
          '& a': {
            color: '#58a6ff',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          },
          '& code': {
            backgroundColor: 'rgba(110, 118, 129, 0.4)',
            padding: '0.2em 0.4em',
            borderRadius: '3px',
            fontSize: '85%',
            fontFamily:
              'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
          },
          '& pre': {
            backgroundColor: '#161b22',
            padding: '16px',
            borderRadius: '6px',
            overflow: 'auto',
            '& code': {
              backgroundColor: 'transparent',
              padding: 0,
            },
          },
          '& blockquote': {
            padding: '0 1em',
            color: '#8b949e',
            borderLeft: '0.25em solid #30363d',
            margin: '16px 0',
          },
          '& table': {
            borderCollapse: 'collapse',
            width: '100%',
            marginBottom: '16px',
          },
          '& th, & td': {
            border: '1px solid #30363d',
            padding: '6px 13px',
          },
          '& th': {
            backgroundColor: '#161b22',
            fontWeight: 600,
          },
          '& tr:nth-of-type(2n)': {
            backgroundColor: '#161b22',
          },
        }}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[[rehypeSanitize]]}
          components={components}
        >
          {dataResult.content}
        </ReactMarkdown>
      </Box>
    </Paper>
  );
};
